import React, { useState, useEffect } from 'react';
import { Users, Building2, FolderOpen, Clock, DollarSign, TrendingUp, Calendar, CheckCircle, Plus, ArrowRight, Timer, Bell, Search } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Client, Project, NewClient, NewProject } from '../../types';
import api from '../../services/api';
import { ProjectForm } from '../projects/ProjectForm/ProjectForm';
import { ClientForm } from '../clients/ClientForm/ClientForm';
import projectService from '../../services/projectService';
import { ClientsService } from '../../services/api/client';
import { Button, Badge, Card, EmptyState, LoadingSpinner, PageHeader } from '../common';

export const Dashboard: React.FC = () => {
  const [statsData, setStatsData] = useState({
    totalClients: 0,
    activeClients: 0,
    recentClients: [] as Client[],
    allClients: [] as Client[],
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalHours: 0,
    totalRevenue: 0,
    recentProjects: [] as Project[],
  });
  const [loading, setLoading] = useState(true);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch clients
        const clientsResponse = await api.get<any>('/api/clients', token || undefined);
        const clientsArray = Array.isArray(clientsResponse) ? clientsResponse : clientsResponse.clients || [];

        // Fetch projects
        let projectsArray: Project[] = [];
        try {
          const projectsResponse = await api.get<any>('/api/projects', token || undefined);
          projectsArray = Array.isArray(projectsResponse) ? projectsResponse : projectsResponse.projects || [];
        } catch (error) {
          console.error('Failed to fetch projects:', error);
        }

        // Calculate totals
        const activeProjects = projectsArray.filter(p => p.status === 'in_progress').length;
        const completedProjects = projectsArray.filter(p => p.status === 'completed').length;
        const totalRevenue = projectsArray.reduce((sum, p) => {
          const budget = typeof p.budget === 'number' ? p.budget : (p.budget ? parseFloat(p.budget as any) : 0) || 0;
          return sum + budget;
        }, 0);
        const totalHours = projectsArray.reduce((sum, p) => {
          const hours = typeof p.actualHours === 'number' ? p.actualHours : (p.actualHours ? parseFloat(p.actualHours as any) : 0) || 0;
          return sum + hours;
        }, 0);

        setStatsData({
          totalClients: clientsArray.length,
          activeClients: clientsArray.filter((c: Client) => c.status === 'active').length,
          recentClients: clientsArray.slice(0, 4),
          allClients: clientsArray,
          totalProjects: projectsArray.length,
          activeProjects,
          completedProjects,
          totalHours,
          totalRevenue,
          recentProjects: projectsArray.slice(0, 3),
        });

      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  const handleCreateProject = async (projectData: NewProject) => {
    try {
      setCreating(true);
      await projectService.createProject(projectData as any);
      setShowProjectForm(false);
      // Refresh stats
      const projectsResponse = await api.get<any>('/api/projects', token || undefined);
      const projectsArray = Array.isArray(projectsResponse) ? projectsResponse : projectsResponse.projects || [];
      setStatsData(prev => ({
        ...prev,
        totalProjects: projectsArray.length,
        activeProjects: projectsArray.filter((p: Project) => p.status === 'in_progress').length,
        recentProjects: projectsArray.slice(0, 3),
      }));
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Failed to create project. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateClient = async (clientData: NewClient) => {
    try {
      setCreating(true);
      await ClientsService.createClient(clientData, token || undefined);
      setShowClientForm(false);
      // Refresh stats
      const clientsResponse = await api.get<any>('/api/clients', token || undefined);
      const clientsArray = Array.isArray(clientsResponse) ? clientsResponse : clientsResponse.clients || [];
      setStatsData(prev => ({
        ...prev,
        totalClients: clientsArray.length,
        activeClients: clientsArray.filter((c: Client) => c.status === 'active').length,
        recentClients: clientsArray.slice(0, 4),
      }));
    } catch (error) {
      console.error('Failed to create client:', error);
      alert('Failed to create client. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoadingSpinner message="Loading dashboard..." />
      </div>
    );
  }

  const stats = [
    {
      label: 'Total Clients',
      value: statsData.totalClients.toString(),
      change: `${statsData.activeClients} active`,
      icon: Users,
      color: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    {
      label: 'Active Projects',
      value: statsData.activeProjects.toString(),
      change: `${statsData.completedProjects} completed`,
      icon: FolderOpen,
      color: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    {
      label: 'Total Hours',
      value: statsData.totalHours.toFixed(1),
      change: 'All projects',
      icon: Clock,
      color: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
    {
      label: 'Total Revenue',
      value: `$${Math.round(statsData.totalRevenue).toLocaleString('en-US')}`,
      change: 'Project budgets',
      icon: DollarSign,
      color: 'bg-green-100',
      iconColor: 'text-green-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader
        title="Dashboard"
        subtitle="Welcome back! Here's what's happening today."
        actions={
          <>
            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              <Search className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-sm font-bold text-indigo-600">U</span>
            </div>
          </>
        }
      />

      <div className="px-6 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {stats.map((stat, index) => (
            <Card key={index} variant="default">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                </div>
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
              <p className="text-xs text-gray-600 font-medium">{stat.change}</p>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card variant="default" className="mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Quick Actions</h2>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setShowProjectForm(true)}
              >
                New Project
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setShowClientForm(true)}
              >
                New Client
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<Timer className="w-4 h-4" />}
                onClick={() => alert('Time tracking feature coming soon!')}
              >
                Start Timer
              </Button>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Active Projects */}
          <Card
            variant="default"
            className="col-span-2"
            title={<h2 className="text-sm font-semibold text-gray-900">Active Projects</h2>}
            actions={
              <button className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </button>
            }
          >
            {statsData.recentProjects.length > 0 ? (
              <div className="space-y-3">
                {statsData.recentProjects.map((project) => (
                  <div key={project.id} className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">{project.name}</h3>
                        <p className="text-xs text-gray-500">{project.client?.name || 'No client'}</p>
                      </div>
                      <Badge status={project.status} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {project.dueDate ? new Date(project.dueDate).toLocaleDateString() : 'No due date'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {project.actualHours || 0}h logged
                      </span>
                      <span>{(project as any).progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all"
                        style={{ width: `${(project as any).progress || 0}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={FolderOpen}
                title="No active projects"
                description=""
              />
            )}
          </Card>

          {/* Upcoming Tasks Placeholder */}
          <Card
            variant="default"
            title={<h2 className="text-sm font-semibold text-gray-900">Upcoming Tasks</h2>}
            actions={<button className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All</button>}
          >
            <EmptyState
              icon={CheckCircle}
              title="Task management coming soon"
              description=""
            />
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Recent Clients */}
          <Card
            variant="default"
            title={<h2 className="text-sm font-semibold text-gray-900">Recent Clients</h2>}
            actions={
              <button className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </button>
            }
          >
            {statsData.recentClients.length > 0 ? (
              <div className="space-y-2">
                {statsData.recentClients.map((client) => (
                  <div key={client.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {client.name ? client.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{client.name}</p>
                        <p className="text-xs text-gray-500">{client.email}</p>
                      </div>
                    </div>
                    <Badge status={client.status} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Building2}
                title="No clients yet"
                description=""
              />
            )}
          </Card>

          {/* Recent Activity Placeholder */}
          <Card
            variant="default"
            title={<h2 className="text-sm font-semibold text-gray-900">Recent Activity</h2>}
            actions={<button className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All</button>}
          >
            <EmptyState
              icon={Clock}
              title="Activity tracking coming soon"
              description=""
            />
          </Card>
        </div>
      </div>

      {/* Modals */}
      {showProjectForm && (
        <ProjectForm
          onClose={() => setShowProjectForm(false)}
          onSubmit={handleCreateProject}
          loading={creating}
          clients={statsData.allClients}
        />
      )}

      {showClientForm && (
        <ClientForm
          onClose={() => setShowClientForm(false)}
          onSubmit={handleCreateClient}
          loading={creating}
        />
      )}
    </div>
  );
};
